import { useVirtualizer } from '@tanstack/react-virtual';
import React from 'react';

import { Bar } from './bar';
import { cssVars } from './css-vars';
import { EmptyRowAdd } from './empty-row-add';
import { GroupBar } from './group-bar';
import { OffscreenIndicator } from './offscreen-indicator';
import { offscreenSideOf } from './offscreen';
import { RowHeader } from './row-header';
import styles from './row-list.module.scss';
import { barLayout } from '../model/bar-layout';

import type { GanttPatch, GanttRenderers, Row, TimeScale } from '../types';

/**
 * `ROW_HEIGHT_PX` is the virtualizer's row-to-row STRIDE, and the one row dimension JS owns.
 * `gantt-chart.tsx` writes it to `--gantt-row-stride` on `.root`, alongside every other custom
 * property JS owns, and `.row` sizes itself from that — so the offset a row is placed at and the
 * height it is painted at come from a single number and cannot drift the way three
 * independently-edited stylesheet constants could in the old module.
 *
 * This component deliberately writes NOTHING: it used to write `--gantt-row-height` and
 * `--gantt-row-gap` inline on `.rows`, a descendant, where an inline value shadows anything set
 * above it — including on `.root` — which made the two dimensions spec §6.1 calls customizable
 * the only two a stylesheet could not change. Both now live in `gantt-chart.module.scss`, with
 * the gap as a plain overridable token and the height derived as stride minus gap.
 *
 * `ROW_GAP_REM` has to stay equal to `gantt-chart.module.scss`'s `--gantt-row-gap` default for
 * the DEFAULT row to be `ROW_HEIGHT_REM` tall. Only the default: a consumer that overrides the
 * gap is meant to change the painted height, which is the point of deriving it.
 *
 * 62.5% rem base: 1rem = 10px.
 */
const REM_PX = 10;
export const ROW_HEIGHT_REM = 4;
export const ROW_GAP_REM = 0.4;
export const ROW_HEIGHT_PX = (ROW_HEIGHT_REM + ROW_GAP_REM) * REM_PX;

type RowListProps = {
  rows: Row[];
  scrollRef: React.RefObject<HTMLDivElement | null>;
  renderers?: GanttRenderers;
  onToggleCollapsed: (id: string) => void;
  scale: TimeScale;
  windowStartDay: number;
  /** Current scroll position of `scrollRef`'s element, for the off-screen indicator check.
   * Defaults to 0/0 so a caller that has no scroll metrics yet simply renders no indicators,
   * rather than every row's bar reading as off-screen. */
  scrollLeft?: number;
  viewportWidth?: number;
  /** Width of the sticky sidebar occluding the left edge of the timeline. */
  sidebarWidth?: number;
  /** Invoked with the row whose indicator was clicked, so the owner can scroll its bar into view. */
  onScrollToRow?: (row: Row) => void;
  /**
   * How many grid rows precede the first body row, so `aria-rowindex` agrees with the owner's
   * `aria-rowcount`. `aria-rowindex` is 1-based and counts EVERY row in the grid, header rows
   * included: `gantt-chart.tsx` puts the timeline header at index 1, so it passes 1 and body
   * rows start at 2. Required rather than defaulted, because getting it wrong is silent — the
   * header row and the first body row simply both claim index 1.
   */
  rowIndexOffset: number;
  /**
   * `GanttConfig.readOnly`, forwarded to `Bar`/`GroupBar` so they can fold it into
   * `isDragDisabledFor` alongside `row.item.editable`/`row.isGroup` — the one function both read,
   * so a row's drag eligibility cannot drift between the two renderers.
   */
  readOnly?: boolean;
  /** Forwarded to `Bar`/`GroupBar`'s own `useBarDrag` call; see `GanttChartProps.onCommit`. */
  onCommit?: (patches: GanttPatch[]) => Promise<void> | void;
};

/**
 * The single row loop. A row's sticky header cell and its bar track are children of ONE element,
 * so nothing can put them on different lines — which is the whole reason this module exists.
 *
 * Rows are a fixed height, so the virtualizer needs no measurement pass and every row's offset
 * is exactly `index * ROW_HEIGHT_PX`.
 */
export const RowList: React.FC<RowListProps> = ({
  rows,
  scrollRef,
  renderers,
  onToggleCollapsed,
  scale,
  windowStartDay,
  scrollLeft = 0,
  viewportWidth = 0,
  sidebarWidth = 0,
  onScrollToRow,
  rowIndexOffset,
  readOnly,
  onCommit,
}) => {
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT_PX,
    overscan: 8,
  });

  // `scrollRef` is owned by an ancestor (the interface requires it, so callers can put the
  // scrollable element wherever they need it). React commits child layout effects before a
  // parent's own ref attaches, so on first mount the virtualizer's internal subscription effect
  // — which has no dependency array and re-subscribes on every render — sees `scrollRef.current`
  // as still null and never finds the scroll element. Forcing one extra render after mount gives
  // that effect a second pass, by which point the ancestor's ref has committed.
  const [, forceRemeasure] = React.useReducer((count: number) => count + 1, 0);
  React.useLayoutEffect(forceRemeasure, []);

  // Resolved once per render, not once per row: all three are the same for every row, and the
  // suffix in particular has to reach the group bar as a COMPONENT so a row that never renders
  // never constructs it.
  const BarComponent = renderers?.bar ?? Bar;
  const GroupBarComponent = renderers?.groupBar ?? GroupBar;
  const groupBarSuffix = renderers?.groupBarSuffix;

  return (
    <div className={styles['rows']} style={{ height: virtualizer.getTotalSize() }}>
      {virtualizer.getVirtualItems().map((virtualRow) => {
        const row = rows[virtualRow.index];
        // Day-domain only (spec §6.1 / the Task 11 ruling): `--bar-offset` / `--bar-span` carry
        // `startOffsetDays` / `spanDays`, and the bar's own CSS multiplies by
        // `--gantt-px-per-day`. Never write `layout.x` / `layout.width` (pixel domain) here — that
        // would bake `pxPerDay` into every bar's inline style and force a re-render of every bar
        // on a zoom change, exactly what this design exists to avoid. The pixel fields still exist
        // on `BarLayout` for Task 13's off-screen indicators, which compare against `scrollLeft`.
        const layout = row.span ? barLayout(row.span, scale, windowStartDay) : null;

        return (
          <div
            key={row.id}
            role="row"
            aria-rowindex={row.index + rowIndexOffset + 1}
            className={styles['row']}
            data-row-id={row.id}
            data-row-index={row.index}
            data-depth={row.depth}
            data-group={row.isGroup}
            data-collapsed={row.isCollapsed}
            style={{ transform: `translateY(${virtualRow.start}px)` }}
          >
            <RowHeader row={row} renderers={renderers} onToggleCollapsed={onToggleCollapsed} />

            {/* The row's second cell. `gridcell` and not `rowheader`: the header cell above
                names the row, this one holds its bar. */}
            <div
              role="gridcell"
              data-role="track"
              className={styles['track']}
              style={
                layout
                  ? cssVars({
                      '--bar-offset': layout.startOffsetDays,
                      '--bar-span': layout.spanDays,
                    })
                  : undefined
              }
            >
              {row.isGroup ? (
                <GroupBarComponent
                  row={row}
                  state={{ isDragging: false, isResizing: false, isFocused: false }}
                  suffix={groupBarSuffix}
                  scale={scale}
                  readOnly={readOnly}
                  onCommit={onCommit}
                />
              ) : (
                <BarComponent
                  row={row}
                  state={{ isDragging: false, isResizing: false, isFocused: false }}
                  scale={scale}
                  readOnly={readOnly}
                  onCommit={onCommit}
                />
              )}

              {/* `Bar` (default or custom) draws nothing for an undated leaf row — this is the
                  only thing that ever fills that empty track. Keyed on `!layout`, the same
                  undated/dated split `row-list.tsx` already uses for the off-screen check below,
                  not on `BarComponent`'s identity: a custom `renderers.bar` gets the same
                  restored affordance rather than losing it silently. `EmptyRowAdd` itself is the
                  one place that re-checks `row.isGroup` (via `isAddDisabledFor`) and renders
                  nothing for a group row, so this is never reached for one in practice. */}
              {!row.isGroup && !layout ? (
                <EmptyRowAdd
                  row={row}
                  scale={scale}
                  windowStartDay={windowStartDay}
                  readOnly={readOnly}
                  onCommit={onCommit}
                />
              ) : null}

              {layout
                ? (() => {
                    const side = offscreenSideOf({
                      barX: layout.x,
                      barWidth: layout.width,
                      scrollLeft,
                      viewportWidth,
                      sidebarWidth,
                    });

                    return side ? (
                      <OffscreenIndicator
                        row={row}
                        side={side}
                        onScrollTo={(target) => onScrollToRow?.(target)}
                      />
                    ) : null;
                  })()
                : null}
            </div>
          </div>
        );
      })}
    </div>
  );
};
